<template>
  <form @submit.prevent>
    <v-row class="mb-0">
      <v-col class="col-12 md:col-6">
        <label for="locale">{{ $t("labels.clientData") }}</label>
        <v2-select
          withRequiredLabel
          :placeholder="$t('client.locale')"
          v-model="locale"
          :options="localeOptions"
          optionLabel="label"
          id="locale"
          name="locale"
          v-validate="'required'"
          :error="errors.first('locale')"
          :data-vv-as="$t('client.locale')"
        ></v2-select>
        <v-textfield
          :placeholder="$t('client.firstName')"
          v-model="firstName"
          name="firstname"
          :error="errors.first('firstname')"
          :required="optionalValidation.firstname && optionalValidation.firstname.indexOf('required') !== -1"
          v-validate="optionalValidation.firstname ? optionalValidation.firstname : ''"
          :data-vv-as="$t('client.firstName')"
        />
        <v-textfield
          :placeholder="$t('client.lastName')"
          v-model="lastName"
          :required="true"
          v-validate="'required'"
          name="lastname"
          :error="errors.first('lastname')"
          :data-vv-as="$t('client.lastName')"
        />
        <v-textfield
          :placeholder="$t('client.email')"
          v-model.trim="email"
          type="email"
          :required="!edit || (currentClient && !currentClient.has_idp_account)"
          v-validate="'required|email'"
          name="email"
          :error="errors.first('email')"
          :data-vv-as="$t('client.email')"
          :disabled="edit && currentClient && currentClient.has_idp_account"
        />
        <v-textfield
          :placeholder="$t('client.company')"
          v-model="company"
          name="company"
          :error="errors.first('company')"
          :required="optionalValidation.company && optionalValidation.company.indexOf('required') !== -1"
          v-validate="optionalValidation.company ? optionalValidation.company : ''"
          :data-vv-as="$t('client.company')"
        />
        <v2-select
          :withRequiredLabel="optionalValidation.gender && optionalValidation.gender.indexOf('required') !== -1"
          :placeholder="$t('client.gender')"
          v-model="gender"
          :options="genderOptions"
          optionLabel="label"
          id="gender"
          name="gender"
          v-validate="optionalValidation.gender ? optionalValidation.gender : ''"
          :error="errors.first('gender')"
          :data-vv-as="$t('client.gender')"
        ></v2-select>
        <v-textfield
          :placeholder="$t('client.street')"
          v-model="street"
          name="street"
          :error="errors.first('street')"
          :required="optionalValidation.street && optionalValidation.street.indexOf('required') !== -1"
          v-validate="optionalValidation.street ? optionalValidation.street : ''"
          :data-vv-as="$t('client.street')"
        />
        <v-textfield
          :placeholder="$t('client.zip')"
          v-model="zip"
          name="zip"
          :error="errors.first('zip')"
          :required="optionalValidation.zip && optionalValidation.zip.indexOf('required') !== -1"
          v-validate="optionalValidation.zip ? optionalValidation.zip : ''"
          :data-vv-as="$t('client.zip')"
        />
        <v-textfield
          :placeholder="$t('client.city')"
          v-model="city"
          name="city"
          :error="errors.first('city')"
          :required="optionalValidation.city && optionalValidation.city.indexOf('required') !== -1"
          v-validate="optionalValidation.city ? optionalValidation.city : ''"
          :data-vv-as="$t('client.city')"
        />
        <v2-select
          :optional="!optionalValidation.country"
          :withRequiredLabel="optionalValidation.country && optionalValidation.country.indexOf('required') !== -1"
          :placeholder="$t('client.country')"
          v-model="country"
          :options="countryOptions"
          optionLabel="label"
          id="country"
          name="country"
          v-validate="optionalValidation.country ? optionalValidation.country : ''"
          :error="errors.first('country')"
          :data-vv-as="$t('client.country')"
        ></v2-select>
        <v-textfield
          :placeholder="$t('client.tel')"
          v-model="tel"
          name="tel"
          :error="errors.first('tel')"
          :required="optionalValidation.tel && optionalValidation.tel.indexOf('required') !== -1"
          v-validate="optionalValidation.tel ? optionalValidation.tel : ''"
          :data-vv-as="$t('client.tel')"
        />
        <v-textfield
          :placeholder="$t('client.mobile')"
          v-model="mobile"
          name="mobile"
          :error="errors.first('mobile')"
          :required="optionalValidation.mobile && optionalValidation.mobile.indexOf('required') !== -1"
          v-validate="optionalValidation.mobile ? optionalValidation.mobile : ''"
          :data-vv-as="$t('client.mobile')"
        />
        <v-textfield
          :placeholder="$t('client.fax')"
          v-model="fax"
          name="fax"
          :error="errors.first('fax')"
          :required="optionalValidation.fax && optionalValidation.fax.indexOf('required') !== -1"
          v-validate="optionalValidation.fax ? optionalValidation.fax : ''"
          :data-vv-as="$t('client.fax')"
        />
        <v-textfield
          :placeholder="$t('client.website')"
          v-model="website"
          name="website"
          :required="optionalValidation.website && optionalValidation.website.indexOf('required') !== -1"
          v-validate="
            optionalValidation.website ? optionalValidation.website + '|url:require_protocol' : 'url:require_protocol'
          "
          :error="errors.first('website')"
          :data-vv-as="$t('client.website')"
        />
        <v-textfield
          :placeholder="$t('client.reference')"
          v-model="reference"
          name="reference"
          :error="errors.first('reference')"
          :required="optionalValidation.reference && optionalValidation.reference.indexOf('required') !== -1"
          v-validate="optionalValidation.reference ? optionalValidation.reference : ''"
          :data-vv-as="$t('client.reference')"
        />
      </v-col>
      <v-col class="col-12 md:col-6">
        <v-textarea :label="$t('labels.notes')" v-model="notes" :rows="'25'"></v-textarea>
        <label for="tags">{{ $t("labels.tags") }}</label>
        <v2-select
          :multiple="true"
          :placeholder="$t('placeholders.addOrSearchTags')"
          id="tags"
          :tagPlaceholder="$t('placeholders.newTag')"
          v-model="tags"
          :options="tagsOptions"
          :taggable="true"
          @tag="addTag"
          :loading="isLoading"
          :hideSelected="true"
          :closeOnSelect="false"
          :clearOnSelect="false"
        ></v2-select>
      </v-col>
      <v-col stretch v-if="!hideSubmitBtn">
        <div class="w-full flex justify-end">
          <v-button v-if="!edit" primary @click.native="submitClient()">{{ $t("actions.createClient") }}</v-button>
          <v-button v-if="edit" primary @click.native="submitClient(false)">{{ $t("actions.save") }}</v-button>
        </div>
      </v-col>
    </v-row>
  </form>
</template>

<script>
import { mapGetters, mapActions } from "vuex";

export default {
  name: "CreateEditClientForm",
  props: {
    edit: Boolean,
    hideSubmitBtn: Boolean,
  },
  data() {
    return {
      isLoading: false,
      locale: { label: this.$t("lang.de"), value: "de" },
      lastName: "",
      firstName: "",
      email: "",
      street: "",
      zip: "",
      city: "",
      country: "",
      company: "",
      tel: "",
      mobile: "",
      fax: "",
      website: "",
      reference: "",
      gender: "",
      notes: "",
      tags: [],
      localeOptions: [],
      genderOptions: [
        { label: this.$t("gender.female"), value: "f" },
        { label: this.$t("gender.male"), value: "m" },
      ],
      countryOptions: [],
      tagsOptions: [],
      optionalValidation: {},
    };
  },
  created() {
    if (this.edit) {
      this.setValues();
    }
    this.transformCountryOptions();
    this.getLanguages();
    this.setValidation();
    this.initTagOptions();
    this.$eventbus.$on("submitClient", this.submitClient);
  },
  destroyed() {
    this.$eventbus.$off("submitClient");
  },
  methods: {
    ...mapActions(["createClient", "editClient", "getClientTags", "getTeamTags", "getTeamLanguages"]),
    transformCountryOptions() {
      const countries = this.$t("countries");
      Object.keys(countries).forEach((key) => {
        this.countryOptions.push({ label: countries[key], value: key });
      });
    },
    getLanguages() {
      this.getTeamLanguages().then(() => {
        if (this.teamLanguages) {
          this.teamLanguages.forEach((lang) => {
            this.localeOptions.push({
              label: this.$t('lang["' + lang + '"]'),
              value: lang,
            });
          });
          if (this.currentClient) {
            this.locale = this.localeOptions.find((obj) => {
              return obj.value === this.currentClient.locale;
            });
          }
        }
      });
    },
    setValidation() {
      if (this.teams[this.$route.params.team].account) {
        const teamAttributes = this.teams[this.$route.params.team].account.team.attributes;
        if (teamAttributes.optional_validation) {
          this.optionalValidation = teamAttributes.optional_validation.clients
            ? teamAttributes.optional_validation.clients
            : {};
        }
      }
    },
    initTagOptions() {
      this.isLoading = true;
      const params = {
        per_page: "1000",
      };
      this.getTeamTags({ params, customLoading: true }).then((res) => {
        this.formatTagsOptions(JSON.parse(JSON.stringify(res)));
        this.isLoading = false;
      });
    },
    formatTagsOptions(tagsObjects) {
      this.tagsOptions = [];
      Object.keys(tagsObjects).forEach((key) => {
        this.tagsOptions.push(tagsObjects[key].name);
      });
    },
    setValues() {
      this.lastName = this.currentClient.last_name;
      this.firstName = this.currentClient.first_name;
      this.email = this.currentClient.email;
      this.street = this.currentClient.street;
      this.zip = this.currentClient.zip;
      this.city = this.currentClient.city;
      this.country = this.currentClient.country;
      this.company = this.currentClient.company;
      this.tel = this.currentClient.tel;
      this.mobile = this.currentClient.mobile;
      this.fax = this.currentClient.fax;
      this.website = this.currentClient.website;
      this.reference = this.currentClient.reference;
      this.notes = this.currentClient.notes;
      // this.locale = this.localeOptions.find((obj) => { return obj.value === this.currentClient.locale })
      this.gender = this.genderOptions.find((obj) => {
        return obj.value === this.currentClient.gender;
      });
      this.country = this.countryOptions.find((obj) => {
        return obj.value === this.currentClient.country;
      });
      this.formatTagsOptions(JSON.parse(JSON.stringify(this.currentClientTags)));
      this.tags = JSON.parse(JSON.stringify(this.tagsOptions));
      console.log("set values", this.locale, this.localeOptions, this.currentClient.locale);
    },
    addTag(newTag) {
      this.tagsOptions.push(newTag);
      this.tags.push(newTag);
    },
    submitClient(create = true) {
      const vm = this;
      const data = {
        last_name: vm.lastName,
        first_name: vm.firstName,
        email: vm.email,
        locale: vm.locale && vm.locale.value,
        street: vm.street,
        zip: vm.zip,
        city: vm.city,
        country: vm.country && vm.country.value,
        company: vm.company,
        tel: vm.tel,
        mobile: vm.mobile,
        fax: vm.fax,
        website: vm.website,
        reference: vm.reference,
        gender: vm.gender && vm.gender.value,
        notes: vm.notes,
      };
      vm.website = vm.website && vm.website.toLowerCase();
      if (vm.website && vm.website.substr(0, 7) !== "http://" && vm.website.substr(0, 8) !== "https://") {
        vm.website = "http://" + vm.website;
        data.website = vm.website;
      }
      vm.$validator.validateAll().then((result) => {
        if (result) {
          if (create) {
            vm.createClient({ redirect: true, data, tags: { tags: vm.tags } })
              .then((res) => {
                this.$emit("afterCreate", res);
              })
              .catch((err) => {
                if (
                  err.response &&
                  !err.response.data.error.custom &&
                  err.response.data.error.http_code === 422 &&
                  err.response.data.error.validation.email
                ) {
                  vm.$validator.errors.add({
                    field: "email",
                    msg: vm.$t("errors.emailExists"),
                  });
                  vm.$snotify.error(vm.$t("toasts.clientForm.validation"), vm.$t("toasts.error"), {
                    timeout: 0,
                    icon: vm.$errorIcon,
                  });
                }
              });
          } else {
            vm.editClient({
              params: { client: vm.currentClient.uid },
              redirect: true,
              data,
              tags: { tags: vm.tags },
            })
              .then((res) => {
                this.$emit("afterEdit", res);
              })
              .catch((err) => {
                if (
                  err.response &&
                  !err.response.data.error.custom &&
                  err.response.data.error.http_code === 422 &&
                  err.response.data.error.validation.email
                ) {
                  vm.$validator.errors.add({
                    field: "email",
                    msg: vm.$t("errors.emailExists"),
                  });
                  vm.$snotify.error(vm.$t("toasts.clientForm.validation"), vm.$t("toasts.error"), {
                    timeout: 0,
                    icon: vm.$errorIcon,
                  });
                }
              });
          }
        } else {
          vm.$snotify.error(vm.$t("toasts.clientForm.validation"), vm.$t("toasts.error"), {
            timeout: 0,
            icon: vm.$errorIcon,
          });
        }
      });
    },
  },
  computed: {
    ...mapGetters({
      teams: "auth_get_teams",
      currentClient: "clients_get_current_client",
      currentClientTags: "clients_get_current_client_tags",
      teamLanguages: "teams_get_languages",
    }),
  },
};
</script>

<style lang="scss"></style>
